/**
 * DECORATIVE BOX BEAMS (2026-10-08, Matt: "allow the user to turn on decorative box beams and
 * set their sizes and spacing on ceilings … box beams built around internal wood nailers. They
 * should start as a default wood color but can be painted with appropriate finishes. Decorative
 * box beams should show up under project settings to set the standard finish, too. And the user
 * should be allowed to customize the beam's locations when they need to be scooted around. They
 * can be reset … an are you sure warning should tell the user that custom settings will be
 * removed (only when they have been employed).")
 *
 * A ceiling's box beams are DERIVED from a handful of instance params — on/off, one direction or
 * both (coffered), width, depth, spacing, direction — laid out centered in the ceiling so the end
 * bays match. The only stored geometry is the user's nudges: `boxBeamMoves`, an offset per beam
 * (by family + index in the derived layout). Because those offsets are keyed to the layout, a
 * change to the LAYOUT (spacing, direction, one-way/coffered) clears them — the palette warns
 * first when there are any (BOX_BEAM_LAYOUT_KEYS). Width and depth keep them: a beam's center
 * stays where it was put.
 *
 * Built (bim-geometry categories/slab/boxBeams.ts) as the real thing: a 2x nailer fastened to the
 * ceiling, two side boards and a bottom board nailed to it, all in the box beam finish.
 *
 * Finish: the ceiling's own pick, else the project's standard (Project Settings → Box Beams), else
 * plain wood. Color: the Paint Brush's coat (`boxBeamColor`), else the finish's own standard (Finish
 * Colors), else the palette's Box Beams role (when the finish can wear it), else the wood itself.
 */
import type { ParamValue, Path2D, Pt2 } from './types'
import { maps, transact, recordToYMap, type ModelDoc } from './doc'
import { ceilingSlopePlane, ceilingSlopeRiseAt, type CeilingSlopePlane } from './ceilingSlope'

// ---------- the finishes ----------

export interface BoxBeamFinish { key: string; firmId: string; label: string; name: string; color: string; grain: string }
/** firm-standard box beam finishes (stable firm ids — the firm library and a model adopt the same
 *  record; picking one adds it to a model that doesn't have it yet, the bare-metal precedent) */
export const BOX_BEAM_FINISHES: BoxBeamFinish[] = [
  // the default — a plain wood that takes the project's Box Beams color (paint or stain)
  { key: 'wood', firmId: 'boxBeamStain01', label: 'Wood', name: 'Box Beam — Wood', color: '#a0714a', grain: '#6e4b2f' },
  { key: 'reclaimed', firmId: 'boxBeamRecl01', label: 'Reclaimed fir', name: 'Box Beam — Reclaimed Fir', color: '#7d5c40', grain: '#4e3826' },
  { key: 'paint', firmId: 'boxBeamPaint01', label: 'Paint-grade poplar', name: 'Box Beam — Paint-Grade Poplar', color: '#e9e3d4', grain: '#b9b09c' },
]
export const DEFAULT_BOX_BEAM_FINISH = BOX_BEAM_FINISHES[0]

/** the material record a box beam finish is, as stored in the firm library and a model */
export function boxBeamFinishRecord(f: BoxBeamFinish): Record<string, unknown> {
  return {
    id: f.firmId, firmId: f.firmId, name: f.name, color: f.color, category: 'Wood',
    surface: { color: f.color }, cut: { pattern: 'wood-grain', fgColor: f.grain, bgColor: f.color },
    substrate: 'wood',
  }
}
/** the model's material for a box beam finish (by firm id, then name), or null */
export function boxBeamFinishMaterialId(model: ModelDoc, f: BoxBeamFinish): string | null {
  for (const [id, m] of maps(model).materials) {
    if (m.get('firmId') === f.firmId || id === f.firmId || m.get('name') === f.name) return id
  }
  return null
}
/** make sure the model has this finish's material; returns its id */
export function ensureBoxBeamFinishMaterial(model: ModelDoc, f: BoxBeamFinish): string {
  const have = boxBeamFinishMaterialId(model, f)
  if (have) return have
  transact(model, () => { maps(model).materials.set(f.firmId, recordToYMap(boxBeamFinishRecord(f))) })
  return f.firmId
}
export const boxBeamFinishOf = (id: unknown): BoxBeamFinish | null =>
  BOX_BEAM_FINISHES.find((f) => f.firmId === id) ?? null

// ---------- the settings ----------

export const BOX_BEAM_PATTERNS = [
  { value: 'oneWay', label: 'One direction' },
  { value: 'grid', label: 'Both directions (coffered)' },
] as const
export type BoxBeamPattern = typeof BOX_BEAM_PATTERNS[number]['value']

/** nominal board + nailer sizes (feet): 3/4" boards, a 1 1/2" nailer */
export const BOX_BEAM_BOARD_FT = 0.75 / 12
export const BOX_BEAM_NAILER_FT = 1.5 / 12

/** the params that set WHERE the beams fall — changing one clears the user's nudges */
export const BOX_BEAM_LAYOUT_KEYS = ['boxBeamPattern', 'boxBeamSpacingFt', 'boxBeamCrossSpacingFt', 'boxBeamDirectionDeg'] as const

export type BoxBeamFamily = 'a' | 'b'
export type BoxBeamMoves = Partial<Record<BoxBeamFamily, Record<string, number>>>

export interface BoxBeamSettings {
  on: boolean
  pattern: BoxBeamPattern
  widthFt: number
  depthFt: number
  spacingFt: number
  crossSpacingFt: number
  directionDeg: number
  moves: BoxBeamMoves
}

const num = (v: unknown, d: number, lo: number, hi: number) => {
  const n = typeof v === 'number' && isFinite(v) ? v : d
  return Math.min(hi, Math.max(lo, n))
}

export function boxBeamMovesOf(params: Record<string, ParamValue | unknown> | undefined): BoxBeamMoves {
  const raw = params?.boxBeamMoves as BoxBeamMoves | null | undefined
  if (!raw || typeof raw !== 'object') return {}
  const out: BoxBeamMoves = {}
  for (const fam of ['a', 'b'] as const) {
    const r = raw[fam]
    if (!r || typeof r !== 'object') continue
    const clean: Record<string, number> = {}
    for (const [k, v] of Object.entries(r)) if (typeof v === 'number' && isFinite(v) && Math.abs(v) > 1e-9) clean[k] = v
    if (Object.keys(clean).length) out[fam] = clean
  }
  return out
}
/** does this ceiling carry any hand-placed beams? (the reset / layout-change warning) */
export function hasBoxBeamMoves(params: Record<string, ParamValue | unknown> | undefined): boolean {
  const m = boxBeamMovesOf(params)
  return !!(m.a || m.b)
}

export function boxBeamSettings(params: Record<string, ParamValue | unknown> | undefined): BoxBeamSettings {
  const p = params || {}
  const widthFt = num(p.boxBeamWidthFt, 8 / 12, 3 / 12, 3)
  const spacingFt = num(p.boxBeamSpacingFt, 4, Math.max(1, widthFt + 2 / 12), 40)
  const cross = typeof p.boxBeamCrossSpacingFt === 'number' && p.boxBeamCrossSpacingFt > 0 ? p.boxBeamCrossSpacingFt : spacingFt
  return {
    on: p.boxBeams === true,
    pattern: p.boxBeamPattern === 'grid' ? 'grid' : 'oneWay',
    widthFt,
    depthFt: num(p.boxBeamDepthFt, 10 / 12, 3 / 12, 4),
    spacingFt,
    crossSpacingFt: num(cross, spacingFt, Math.max(1, widthFt + 2 / 12), 40),
    directionDeg: Number(p.boxBeamDirectionDeg) || 0,
    moves: boxBeamMovesOf(p),
  }
}

// ---------- sloped ceilings ----------
//
// SLOPED CEILINGS (2026-10-08, Matt: "We need them on sloped ceilings."). On a sloped ceiling the
// beams lie ON the slope: laid out in the ceiling's UNFOLDED surface — the plan stretched along
// the slope direction by √(1+m²) about the defining edge — so spacing, width and the user's nudges
// are measured along the ceiling, the way a carpenter measures them; each beam hangs square to
// the slope (its depth along the ceiling's normal). A flat ceiling's surface is the plan itself.

export interface BoxBeamSurface {
  plane: CeilingSlopePlane | null
  /** √(1+m²) — 1 on a flat ceiling */
  k: number
  /** plan point → surface coordinates (identity when flat) */
  unfold: (p: Pt2) => Pt2
  /** surface coordinates → plan point */
  fold: (q: Pt2) => Pt2
  /** the ceiling underside's rise above its base height at a plan point */
  riseAt: (p: Pt2) => number
}

export function boxBeamSurface(boundary: Path2D | null | undefined): BoxBeamSurface {
  const plane = ceilingSlopePlane(boundary)
  if (!plane) {
    const id = (p: Pt2): Pt2 => [p[0], p[1]]
    return { plane: null, k: 1, unfold: id, fold: id, riseAt: () => 0 }
  }
  const k = Math.sqrt(1 + plane.m * plane.m)
  const { a, n } = plane
  const along = (p: Pt2) => (p[0] - a[0]) * n[0] + (p[1] - a[1]) * n[1]
  return {
    plane, k,
    unfold: (p) => { const s = along(p) * (k - 1); return [p[0] + n[0] * s, p[1] + n[1] * s] },
    fold: (q) => { const s = (along(q) / k) * (k - 1); return [q[0] - n[0] * s, q[1] - n[1] * s] },
    riseAt: (p) => ceilingSlopeRiseAt(plane, p),
  }
}

// ---------- the layout ----------

export interface BoxBeamLine {
  family: BoxBeamFamily
  index: number
  /** the beam's centerline, as a coordinate along the family's spacing axis `e` */
  c: number
  /** where the even layout puts it (c − base = the user's nudge) */
  base: number
  /** the run direction and the spacing axis */
  d: Pt2
  e: Pt2
  /** how far the center may travel along `e` (the ceiling's extent, less half the width) */
  lo: number
  hi: number
}

const dot = (a: Pt2, b: Pt2) => a[0] * b[0] + a[1] * b[1]

/** evenly spaced centers across [lo, hi] — whole spacings, the leftover split between the two
 *  end bays; one beam down the middle when the span is narrower than a spacing */
export function boxBeamStations(lo: number, hi: number, spacing: number, width: number): number[] {
  const L = hi - lo
  if (!(L > width * 2)) return []
  let n = Math.ceil(L / spacing - 1e-6) - 1
  if (n < 1) n = 1
  const start = lo + (L - (n - 1) * spacing) / 2
  const out: number[] = []
  for (let i = 0; i < n; i++) out.push(start + i * spacing)
  return out
}

/** every beam of a ceiling's box beam layout (empty when they're off), over its boundary ring */
export function boxBeamLines(params: Record<string, ParamValue | unknown> | undefined, ring: Pt2[]): BoxBeamLine[] {
  const s = boxBeamSettings(params)
  if (!s.on || ring.length < 3) return []
  const th = (s.directionDeg * Math.PI) / 180
  const dA: Pt2 = [Math.cos(th), Math.sin(th)]
  const eA: Pt2 = [-dA[1], dA[0]]
  const fams: { family: BoxBeamFamily; d: Pt2; e: Pt2; spacing: number }[] = [{ family: 'a', d: dA, e: eA, spacing: s.spacingFt }]
  if (s.pattern === 'grid') fams.push({ family: 'b', d: eA, e: dA, spacing: s.crossSpacingFt })
  const out: BoxBeamLine[] = []
  for (const f of fams) {
    let lo = Infinity, hi = -Infinity
    for (const p of ring) { const v = dot(p, f.e); lo = Math.min(lo, v); hi = Math.max(hi, v) }
    const stations = boxBeamStations(lo, hi, f.spacing, s.widthFt)
    const moves = s.moves[f.family] || {}
    const cLo = lo + s.widthFt / 2, cHi = hi - s.widthFt / 2
    stations.forEach((base, index) => {
      const c = Math.min(cHi, Math.max(cLo, base + (moves[String(index)] || 0)))
      out.push({ family: f.family, index, c, base, d: f.d, e: f.e, lo: cLo, hi: cHi })
    })
  }
  return out
}

/** the stretches of a beam's centerline inside the ring (even-odd), as [t0, t1] along `d`
 *  measured from the point c·e */
export function boxBeamCenterSpans(line: Pick<BoxBeamLine, 'c' | 'd' | 'e'>, ring: Pt2[]): [number, number][] {
  const o: Pt2 = [line.e[0] * line.c, line.e[1] * line.c]
  const ts: number[] = []
  for (let i = 0; i < ring.length; i++) {
    const A = ring[i], B = ring[(i + 1) % ring.length]
    const sa = dot([A[0] - o[0], A[1] - o[1]], line.e)
    const sb = dot([B[0] - o[0], B[1] - o[1]], line.e)
    if ((sa > 0) === (sb > 0)) continue
    const k = sa / (sa - sb)
    const P: Pt2 = [A[0] + (B[0] - A[0]) * k, A[1] + (B[1] - A[1]) * k]
    ts.push(dot([P[0] - o[0], P[1] - o[1]], line.d))
  }
  ts.sort((a, b) => a - b)
  const spans: [number, number][] = []
  for (let i = 0; i + 1 < ts.length; i += 2) if (ts[i + 1] - ts[i] > 1e-6) spans.push([ts[i], ts[i + 1]])
  return spans
}

/** a world point on a beam's centerline, at `t` along its run */
export function boxBeamPoint(line: Pick<BoxBeamLine, 'c' | 'd' | 'e'>, t: number): Pt2 {
  return [line.e[0] * line.c + line.d[0] * t, line.e[1] * line.c + line.d[1] * t]
}

/** where a beam's grip sits: the middle of its longest stretch inside the ceiling */
export function boxBeamGripPoint(line: Pick<BoxBeamLine, 'c' | 'd' | 'e'>, ring: Pt2[]): Pt2 | null {
  const spans = boxBeamCenterSpans(line, ring)
  if (!spans.length) return null
  const best = spans.reduce((a, b) => (b[1] - b[0] > a[1] - a[0] ? b : a))
  return boxBeamPoint(line, (best[0] + best[1]) / 2)
}

/** the `boxBeamMoves` that put one beam's center at `c` (null clears them all) */
export function boxBeamMovesWith(params: Record<string, ParamValue | unknown> | undefined, line: BoxBeamLine, c: number): BoxBeamMoves | null {
  const cur = boxBeamMovesOf(params)
  const fam = { ...(cur[line.family] || {}) }
  const off = c - line.base
  if (Math.abs(off) < 1e-6) delete fam[String(line.index)]
  else fam[String(line.index)] = off
  const next: BoxBeamMoves = { ...cur, [line.family]: fam }
  if (!Object.keys(fam).length) delete next[line.family]
  return next.a || next.b ? next : null
}
